// frontend/src/components/formularios/AdjuntosOtTab.jsx
// Pestaña "Adjuntos" reutilizable para OTC y OTP: permite subir archivos
// (fotos, informes) con título y observaciones opcionales, ver la lista con
// fecha e ícono según tipo, abrirlos y eliminarlos. Densidad alineada al
// uploader de Logística (FormularioLogistica.jsx): filas de una línea, sin
// paneles grandes.
import React, { useState } from 'react';
import styled from 'styled-components';
import { iconForContentType, formatFileSize } from '../../utils/fileDisplay';
import { formatDateDisplay } from '../../utils/dateUtils';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const EmptyState = styled.div`
  padding: 8px 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.txtMuted};
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.surface};
  font-size: 12px;
`;

const FileIcon = styled.i`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.txtSecundario};
  flex-shrink: 0;
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
`;

const FileTitleLink = styled.a`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const FileMeta = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.txtMuted};
  flex-shrink: 0;
`;

const FileObservaciones = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.txtSecundario};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemoveButton = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.txtMuted};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.dangerBg};
    color: ${({ theme }) => theme.colors.danger};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EditButton = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.txtMuted};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EditRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.surface};
`;

const AddRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 2px;
`;

const FileButtonLabel = styled.label`
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.bgSecundario};
  color: ${({ theme }) => theme.colors.txtSecundario};
  border-radius: 6px;
  padding: 0 10px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 12px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }

  ${({ $disabled }) => $disabled && `
    opacity: 0.6;
    pointer-events: none;
  `}
`;

const HiddenInput = styled.input`
  display: none;
`;

const SelectedFileName = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.txtSecundario};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 160px;
  flex-shrink: 0;
`;

const Input = styled.input`
  flex: 1;
  min-width: 120px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.bgSecundario};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  border-radius: 6px;
  font-size: 12px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const SmallBtn = styled.button`
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ $primary, theme }) => ($primary ? theme.colors.primary : 'transparent')};
  color: ${({ $primary, theme }) => ($primary ? theme.colors.onPrimary : theme.colors.txtSecundario)};
  border: ${({ $primary, theme }) => ($primary ? 'none' : `1px solid ${theme.colors.borderDefault}`)};
  border-radius: 6px;
  height: 32px;
  padding: 0 12px;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    opacity: 0.88;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.danger};
`;

const DisabledNotice = styled.div`
  padding: 8px 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.txtMuted};
`;

/**
 * adjuntos: [{ id, titulo, observaciones, archivo_nombre_original, archivo_content_type, archivo_size_bytes, creado_en, url }]
 * onSubir(formData) => Promise<adjunto>
 * onEliminar(adjuntoId) => Promise<void>
 */
const AdjuntosOtTab = ({ registroId, adjuntos = [], onSubir, onEliminar, onEditar, disabled = false }) => {
  const [archivoSeleccionado, setArchivoSeleccionado] = useState(null);
  const [titulo, setTitulo] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [eliminandoId, setEliminandoId] = useState(null);
  const [error, setError] = useState('');

  const [editandoId, setEditandoId] = useState(null);
  const [editTitulo, setEditTitulo] = useState('');
  const [editObservaciones, setEditObservaciones] = useState('');
  const [editArchivoNuevo, setEditArchivoNuevo] = useState(null);
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);

  if (!registroId) {
    return (
      <Wrap>
        <DisabledNotice>Guardá la orden para poder adjuntar archivos.</DisabledNotice>
      </Wrap>
    );
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setArchivoSeleccionado(file);
    e.target.value = '';
  };

  const handleCancelarSeleccion = () => {
    setArchivoSeleccionado(null);
    setTitulo('');
    setObservaciones('');
  };

  const handleSubir = async () => {
    if (!archivoSeleccionado || typeof onSubir !== 'function') return;
    setSubiendo(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('archivo', archivoSeleccionado);
      fd.append('titulo', titulo);
      fd.append('observaciones', observaciones);
      await onSubir(fd);
      setArchivoSeleccionado(null);
      setTitulo('');
      setObservaciones('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo subir el archivo.');
    } finally {
      setSubiendo(false);
    }
  };

  const handleEliminar = async (adjuntoId) => {
    if (typeof onEliminar !== 'function') return;
    setEliminandoId(adjuntoId);
    setError('');
    try {
      await onEliminar(adjuntoId);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo eliminar el adjunto.');
    } finally {
      setEliminandoId(null);
    }
  };

  const handleIniciarEdicion = (adj) => {
    setEditandoId(adj.id);
    setEditTitulo(adj.titulo || '');
    setEditObservaciones(adj.observaciones || '');
    setEditArchivoNuevo(null);
    setError('');
  };

  const handleCancelarEdicion = () => {
    setEditandoId(null);
    setEditArchivoNuevo(null);
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setEditArchivoNuevo(file);
    e.target.value = '';
  };

  const handleGuardarEdicion = async (adjuntoId) => {
    if (typeof onEditar !== 'function') return;
    setGuardandoEdicion(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('titulo', editTitulo);
      fd.append('observaciones', editObservaciones);
      if (editArchivoNuevo) fd.append('archivo', editArchivoNuevo);
      await onEditar(adjuntoId, fd);
      setEditandoId(null);
      setEditArchivoNuevo(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo editar el adjunto.');
    } finally {
      setGuardandoEdicion(false);
    }
  };

  return (
    <Wrap>
      {adjuntos.length === 0 && <EmptyState>Sin archivos adjuntos.</EmptyState>}

      {adjuntos.map((adj) => (
        editandoId === adj.id ? (
          <EditRow key={adj.id}>
            <FileButtonLabel $disabled={guardandoEdicion}>
              <i className="bi bi-paperclip"></i>
              Reemplazar archivo
              <HiddenInput type="file" disabled={guardandoEdicion} onChange={handleEditFileChange} />
            </FileButtonLabel>
            <SelectedFileName title={editArchivoNuevo ? editArchivoNuevo.name : adj.archivo_nombre_original}>
              {editArchivoNuevo ? editArchivoNuevo.name : adj.archivo_nombre_original}
            </SelectedFileName>
            <Input
              type="text"
              value={editTitulo}
              disabled={guardandoEdicion}
              onChange={(e) => setEditTitulo(e.target.value)}
              placeholder="Título (opcional)"
            />
            <Input
              type="text"
              value={editObservaciones}
              disabled={guardandoEdicion}
              onChange={(e) => setEditObservaciones(e.target.value)}
              placeholder="Observaciones (opcional)"
            />
            <SmallBtn type="button" disabled={guardandoEdicion} onClick={handleCancelarEdicion}>
              Cancelar
            </SmallBtn>
            <SmallBtn type="button" $primary disabled={guardandoEdicion} onClick={() => handleGuardarEdicion(adj.id)}>
              {guardandoEdicion ? 'Guardando...' : 'Guardar'}
            </SmallBtn>
          </EditRow>
        ) : (
          <FileRow key={adj.id}>
            <FileIcon className={iconForContentType(adj.archivo_content_type)} />
            <FileInfo>
              <FileTitleLink href={adj.url || adj.archivo} target="_blank" rel="noopener noreferrer">
                {adj.titulo || adj.archivo_nombre_original || `Adjunto ${adj.id}`}
              </FileTitleLink>
              {adj.observaciones && <FileObservaciones title={adj.observaciones}>{adj.observaciones}</FileObservaciones>}
            </FileInfo>
            <FileMeta>
              {formatDateDisplay(adj.creado_en)}
              {adj.archivo_size_bytes ? ` · ${formatFileSize(adj.archivo_size_bytes)}` : ''}
            </FileMeta>
            <EditButton
              type="button"
              title="Editar adjunto"
              disabled={disabled || eliminandoId === adj.id}
              onClick={() => handleIniciarEdicion(adj)}
            >
              <i className="bi bi-pencil"></i>
            </EditButton>
            <RemoveButton
              type="button"
              title="Eliminar adjunto"
              disabled={disabled || eliminandoId === adj.id}
              onClick={() => handleEliminar(adj.id)}
            >
              <i className="bi bi-trash3"></i>
            </RemoveButton>
          </FileRow>
        )
      ))}

      {!disabled && (
        <AddRow>
          <FileButtonLabel $disabled={subiendo}>
            <i className="bi bi-paperclip"></i>
            Adjuntar archivo
            <HiddenInput type="file" disabled={subiendo} onChange={handleFileChange} />
          </FileButtonLabel>

          {archivoSeleccionado && (
            <>
              <SelectedFileName title={archivoSeleccionado.name}>{archivoSeleccionado.name}</SelectedFileName>
              <Input
                type="text"
                value={titulo}
                disabled={subiendo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Título (opcional)"
              />
              <Input
                type="text"
                value={observaciones}
                disabled={subiendo}
                onChange={(e) => setObservaciones(e.target.value)}
                placeholder="Observaciones (opcional)"
              />
              <SmallBtn type="button" disabled={subiendo} onClick={handleCancelarSeleccion}>
                Cancelar
              </SmallBtn>
              <SmallBtn type="button" $primary disabled={subiendo} onClick={handleSubir}>
                {subiendo ? 'Subiendo...' : 'Subir'}
              </SmallBtn>
            </>
          )}
        </AddRow>
      )}

      {error && <ErrorText>{error}</ErrorText>}
    </Wrap>
  );
};

export default AdjuntosOtTab;
